import React, { useContext, useEffect, useState } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import dayjs from 'dayjs';
import useStyles from '@front10/helpers/dist/UseStyles';
import nanoid from 'nanoid';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../_commons/emotionCache';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel
} from 'react-accessible-accordion';
import Carousel from '../../../controls/ElasticCarousel';
import { isSSR } from '@front10/utils';
import Disclaimer from '../../../others/Disclaimer';
import { publish } from '../../../../_integrations/everymundo/tracking/pubsub';
import Text from '../../../elements/Text';
import Box from '../../../elements/Box';
import Col from '../../../elements/Col';
import Row from '../../../elements/Row';
import Icon from '../../../elements/Icon';
import FareItemCard from '../../../items/FareItemCard/FareItemCard';
import { useFareItemProps } from '../../_commons/tracking';
import { ButtonTerms } from '../../BreakingDealsContainer/atoms/ButtonTerms';
import { isDayAfter } from '../../../../helpers/date';
import { transformNumbersDays } from '../../../../helpers/utils';
import { formatDate } from '../../../../_integrations/everymundo/tracking/helpers';

const AccordionPromotionsMobile = (props) => {
  const {
    listSettings: fareList = {},
    accordionContentTitleBox,
    accordionContentTitle = {},
    accordionContentTitleCount = {},
    accordionContentSubtitle = {},
    accordionUI = {},
    accordionItemHeadingUI = {},
    accordionItemHeadingButtonUI = {},
    accordionItemPanelUI = {},
    iconDropdown = {},
    data,
    scrollIntoView = false,
    dispatch,
    accordionBehavior,
    showDisclaimer = true,
    disclaimerContentSettings,
    disclaimerSettings,
    buttonTermsSettings,
    carouselSettings,
    isMobile,
    showPromoTitle = true,
    showPromoDescription = true
  } = props;

  const [id] = React.useState(nanoid());
  const { direction, translate, theme } = useContext(Context);

  const [accordionClass] = useStyles(accordionUI, { direction, theme });
  const [AccordionItemHeadingClass] = useStyles(accordionItemHeadingUI, { direction, theme });
  const [AccordionItemHeadingButtonClass] = useStyles(accordionItemHeadingButtonUI, {
    direction,
    theme
  });

  const fareItemOnClickAction = (fareData) => {
    let newDepartureDate = dayjs().toDate();
    if (
      fareData.departureDate &&
      isDayAfter(dayjs(fareData.departureDate).toDate(), dayjs().toDate())
    ) {
      newDepartureDate = dayjs(fareData.departureDate).toDate();
    }

    const popupData = {
      name: fareData.name,
      destinationImage: fareData.destinationImage,
      destination: fareData.destination,
      destinationCity: fareData.destinationCity,
      origin: fareData.origin,
      originCity: fareData.originCity,
      departureStart: fareData.departureDate,
      departureEnd: fareData.returnDate,
      departureDate: formatDate(newDepartureDate),
      returnDate: fareData.minStay
        ? formatDate(
            dayjs(newDepartureDate.toString())
              .add(transformNumbersDays(fareData.minStay), 'd')
              .toDate()
          )
        : formatDate(newDepartureDate),
      minStay: fareData.minStay,
      maxStay: fareData.maxStay,
      formattedTotalPrice: fareData.formattedTotalPrice,
      journeyType: fareData.journeyType,
      farenetTravelClass: fareData.farenetTravelClass,
      currencyCode: fareData.currencyCode,
      terms: fareData.fareTerms,
      bookUntilDate: fareData.bookUntilDate,
      airlineCode: fareData.airline,
      siteEdition: fareData.market,
      passengers: {
        options: [
          {
            name: 'passenger1',
            label: 'passenger1-key',
            description: 'passenger1-desc-key',
            maxCount: 9,
            minCount: fareData.min_adult,
            enabled: fareData.adults
          },
          {
            name: 'passenger2',
            label: 'passenger2-key',
            description: 'passenger2-desc-key',
            enabled: fareData.children,
            maxCount: 8
          },
          {
            name: 'passenger3',
            label: 'passenger3-key',
            description: 'passenger3-desc-key',
            enabled: fareData.infant,
            maxCount: 9
          },
          {
            name: 'passenger4',
            label: 'passenger4-key',
            description: 'passenger4-desc-key',
            enabled: fareData.infant_with_seat,
            maxCount: 9
          },
          {
            name: 'passenger5',
            label: 'passenger5-key',
            description: 'passenger5-desc-key',
            enabled: fareData.overseas_filipino_worker,
            maxCount: 9
          },
          {
            name: 'passenger6',
            label: 'passenger6-key',
            description: 'passenger6-desc-key',
            enabled: fareData.youth_or_youth_of_uk === 1 ? true : false,
            maxCount: 8
          },
          {
            name: 'passenger7',
            label: 'passenger7-key',
            description: 'passenger7-desc-key',
            enabled: fareData.youth_or_youth_of_uk === 2 ? true : false,
            maxCount: 8
          }
        ],
        rules: [
          `maxInfantPerAdult:${fareData.max_infant_per_adult}`,
          `maxChildrenPerAdult:${fareData.max_children_per_adult}`,
          `maxInfantAndChildrenPerAdult:${fareData.max_infant_and_children_per_adult}`
        ],
        totalPassengers: fareData.max_passengers
      }
    };
    publish('em.fare.popup.open', popupData);
  };

  let itemSettings = useFareItemProps(
    carouselSettings.itemSettings,
    dispatch,
    fareItemOnClickAction
  );

  if (data.fares && data.fares.length === 1) {
    itemSettings = {
      ...itemSettings,
      fareItemBackgroundImage: {
        ...itemSettings.fareItemBackgroundImage,
        imageResizingParams: {
          worker: 'https://assets.airtrfx.com/cdn-cgi/image/',
          height: 720,
          width: 1280,
          quality: 90,
          fit: 'crop',
          format: 'auto',
          opt: true
        },
        ...itemSettings.fareItemBackgroundImageOneItem
      }
    };
  }

  const { rowsNumber = 2, columnNumbers = 3 } = fareList;
  const [count, setCount] = useState(data.map(() => rowsNumber * columnNumbers));

  useEffect(() => {
    const nextPag = count.map((c, i) => rowsNumber * columnNumbers);
    setCount(nextPag);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnNumbers, data, rowsNumber]);

  const handleFocus = (arrayId) => {
    if (scrollIntoView && !isSSR()) {
      const id = arrayId[0];
      if (id) {
        const ele = document.querySelector(`#accordion__heading-${id}`);
        if (ele) {
          ele.scrollIntoView();
        }
      }
    }
  };

  let accordionSettings;
  switch (accordionBehavior) {
    case 'allExpanded':
      let preExpanded = [];
      for (let index = 0; index < data.length; index++) {
        preExpanded.push(`accordion-item-${id}-${index}`);
      }
      accordionSettings = {
        preExpanded: preExpanded,
        allowMultipleExpanded: true,
        allowZeroExpanded: true
      };
      break;
    case 'allCollapsed':
      accordionSettings = { allowZeroExpanded: true, allowMultipleExpanded: true };
      break;
    default:
      accordionSettings = {
        preExpanded: [`accordion-item-${id}-0`],
        allowZeroExpanded: true,
        allowMultipleExpanded: true
      };
      break;
  }

  useEffect(() => {
    if (!isSSR()) {
      const ele = document.querySelector(`#accordion__heading-accordion-item-${id}-0`);
      if (ele && ele.attributes['aria-expanded'].value === 'false') {
        ele.click();
      }
    }
  }, [accordionBehavior, data, id]);

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={theme.designTokens ?? undefined}>
        <Accordion {...accordionSettings} className={accordionClass} onChange={handleFocus}>
          {data.map((item, index) => {
            return (
              <AccordionItem
                uuid={`accordion-item-${id}-${index}`}
                activeClassName="accordion-item-active"
                key={item.index}
              >
                <AccordionItemHeading className={AccordionItemHeadingClass}>
                  <AccordionItemButton
                    className={`AccordionItemButton ${AccordionItemHeadingButtonClass}`}
                  >
                    <Box {...accordionContentTitleBox}>
                      {showPromoTitle && <Text {...accordionContentTitle}>{item.name}</Text>}
                      <Text {...accordionContentTitleCount}>{`(${item.fares.length} ${translate(
                        'cmp-breaking-deals-offers',
                        'offers'
                      )})`}</Text>
                    </Box>
                    <Icon
                      className="fr-icon-dropdown"
                      icon={'keyboard_arrow_down'}
                      viewBox="0 0 1024 1024"
                      {...iconDropdown}
                    />
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <Box {...accordionItemPanelUI}>
                    {showPromoDescription && (
                      <Text {...accordionContentSubtitle}>{item.description}</Text>
                    )}
                    <Carousel
                      {...carouselSettings}
                      itemSettings={itemSettings}
                      ItemComponentInstance={FareItemCard}
                      itemsData={item.fares}
                    />
                  </Box>
                  {showDisclaimer && item.disclaimer && (
                    <Row {...disclaimerContentSettings}>
                      <Col>
                        <Disclaimer content={item.disclaimer} {...disclaimerSettings} />
                      </Col>
                    </Row>
                  )}
                  <ButtonTerms terms={item.terms} isMobile={isMobile} {...buttonTermsSettings} />
                </AccordionItemPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      </ThemeProvider>
    </EmotionCache>
  );
};

export default AccordionPromotionsMobile;
